import { IMeta } from '@models/IMeta';
import { IProductCard } from '@models/IProductCard';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import ProductCard from '@molecules/ProductCard/ProductCard';
import slugify from '@utils/helpers/slugify';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly embedded?: boolean;
  readonly title?: string | JSX.Element;
  readonly titleClassName?: string;
  readonly titleWrapperClassName?: string;
  readonly hideHeaderOnMobile?: boolean;
  readonly items: IProductCard[];
  readonly meta: IMeta;
  readonly anchorId?: string;
  readonly isScrollAnimationEnabled?: boolean;
}

const ProductCarousel = ({
  embedded = false,
  title = '',
  titleClassName = 'h2',
  titleWrapperClassName,
  hideHeaderOnMobile,
  items,
  meta,
  anchorId = 'featured-product-carousel',
  isScrollAnimationEnabled = false,
}: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.25 });
  const [isLoadedInView, setIsLoadedInView] = useState(false);

  useEffect(() => {
    if (isInView) {
      setIsLoadedInView(true);
    }
  }, [isInView]);

  return (
    <section
      ref={ref}
      className={classNames(
        { 'component-padding': !embedded },
        'bg-white text-black-100 dark:bg-black-100 dark:text-white'
      )}
      id={`a-${anchorId}`}
      data-testid="CT-35-Product-Carousel-Block"
    >
      <NavigationCarousel
        id={`product-carousel-${meta?.contentLink ?? ''}`}
        items={items}
        shadows
        slidesToShowDesktop={4}
        title={title}
        titleClassName={titleClassName}
        titleWrapperClassName={
          isScrollAnimationEnabled ? 'container' : titleWrapperClassName
        }
        hideHeaderOnMobile={hideHeaderOnMobile}
        isScrollAnimationEnabled={isScrollAnimationEnabled}
        isLoadedInView={isLoadedInView}
      >
        {(itemsToDisplay) =>
          itemsToDisplay.map((item, index) => (
            <SwiperSlide
              key={slugify(`${item.name}-${index}`)}
              className="stretch-card"
            >
              {isScrollAnimationEnabled ? (
                <motion.div
                  className="h-full"
                  initial={{ opacity: 0, x: 100 }}
                  animate={isLoadedInView ? { opacity: 1, x: 0 } : {}}
                  transition={{
                    duration: 0.75,
                    ease: 'easeInOut',
                    delay: (index + 1) * 0.2,
                  }}
                >
                  <ProductCard
                    {...item}
                    hidePriceFromSavePill={item.hidePriceFromSavePill}
                    dataTestId={`CT-35-Product-Carousel-Card-${index}`}
                  />
                </motion.div>
              ) : (
                <ProductCard
                  {...item}
                  hidePriceFromSavePill={item.hidePriceFromSavePill}
                  dataTestId={`CT-35-Product-Carousel-Card-${index}`}
                />
              )}
            </SwiperSlide>
          ))
        }
      </NavigationCarousel>
    </section>
  );
};

export default ProductCarousel;
